const AUTH_REDIRECT_TARGET_STORAGE_KEY = "authRedirectTarget";

/**
 * Persists the final in-app destination to use after auth completes.
 *
 * Callers are responsible for deciding which internal path shape should be
 * stored. This helper only enforces that the value stays on the current origin
 * before persisting. Unsafe values clear any existing stored redirect target
 * and are not persisted. Server-side execution is ignored.
 *
 * This is not the Auth0 callback route. It is the internal WorkUI route that
 * the user should land on after the callback/token-exchange flow has finished.
 *
 * @param redirectTarget Internal path to persist for post-auth navigation.
 * @returns `true` when the target is stored successfully, otherwise `false`.
 */
export function setAuthRedirectTarget(redirectTarget: string): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  if (!isSafeInternalPath(redirectTarget)) {
    window.sessionStorage.removeItem(AUTH_REDIRECT_TARGET_STORAGE_KEY);
    return false;
  }

  window.sessionStorage.setItem(AUTH_REDIRECT_TARGET_STORAGE_KEY, redirectTarget);
  return true;
}

/**
 * Reads the final in-app destination that was stored for post-auth navigation.
 *
 * Returns `null` during server rendering, when no value has been stored, or
 * when the stored value is no longer a safe internal path. Unsafe stored
 * values are removed so stale redirect state does not survive into the next
 * auth flow.
 *
 * This value represents the route the UI should navigate to after auth
 * finishes, not the Auth0 callback route.
 *
 * @returns The stored internal redirect target, or `null` when no safe value
 * is available.
 */
export function getAuthRedirectTarget(): string | null {
  if (typeof window === "undefined") {
    return null;
  }

  const value = window.sessionStorage.getItem(AUTH_REDIRECT_TARGET_STORAGE_KEY);
  if (!value) {
    return null;
  }

  if (!isSafeInternalPath(value)) {
    window.sessionStorage.removeItem(AUTH_REDIRECT_TARGET_STORAGE_KEY);
    return null;
  }

  return value;
}

/**
 * Removes the stored post-auth destination after navigation is complete.
 */
export function clearAuthRedirectTarget(): void {
  if (typeof window !== "undefined") {
    window.sessionStorage.removeItem(AUTH_REDIRECT_TARGET_STORAGE_KEY);
  }
}

/**
 * Returns true only for app-relative paths that stay on the current origin.
 *
 * This accepts values such as `/dashboard` and rejects protocol-relative values
 * such as `//example.com` or `/\\example.com`, which could otherwise be used as
 * an open redirect target.
 *
 * @param path Path candidate to validate.
 * @returns `true` when the path is app-relative and safe to treat as an
 * internal redirect target.
 */
function isSafeInternalPath(path: string): boolean {
  if (!path.startsWith("/") || path.startsWith("//") || path.includes("\\")) {
    return false;
  }

  try {
    return new URL(path, window.location.origin).origin === window.location.origin;
  } catch {
    return false;
  }
}
